import {
  ArrowUpCircle,
  Backpack,
  Bookmark,
  BookUser,
  BedDouble,
  Briefcase,
  Building2,
  Camera,
  ChartColumn,
  Clapperboard,
  ClipboardList,
  Coins,
  Compass,
  FileText,
  Gift,
  Globe,
  GraduationCap,
  Handshake,
  Map,
  MapPin,
  MessagesSquare,
  Network,
  Plane,
  Rocket,
  Search,
  ShieldCheck,
  Sparkles,
  Store,
  Target,
  TrendingUp,
  Unplug,
  Users,
  XCircle,
  type LucideIcon,
} from 'lucide-react'
import type { ReactNode } from 'react'

import type { AudienceAccent } from '@/lib/home/content.types'
import { cn } from '@/lib/utils'

/** Icons referenced by name from the content modules. */
const ICONS: Record<string, LucideIcon> = {
  ArrowUpCircle,
  Backpack,
  Bookmark,
  BookUser,
  BedDouble,
  Briefcase,
  Building2,
  Camera,
  ChartColumn,
  Clapperboard,
  ClipboardList,
  Coins,
  Compass,
  FileText,
  Gift,
  Globe,
  GraduationCap,
  Handshake,
  Map,
  MapPin,
  MessagesSquare,
  Network,
  Plane,
  Rocket,
  Search,
  ShieldCheck,
  Sparkles,
  Store,
  Target,
  TrendingUp,
  Unplug,
  Users,
  XCircle,
}

export function Icon({ name, className }: { name: string; className?: string }) {
  const Cmp = ICONS[name] ?? Sparkles
  return <Cmp data-component-id="src/components/home/shared.tsx:81:9" data-component-path="src/components/home/shared.tsx" data-component-line="81" data-component-end-line="81" data-component-file="shared.tsx" data-component-name="Cmp" data-component-content="%7B%22elementName%22%3A%22Cmp%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} aria-hidden strokeWidth={1.75} />
}

/**
 * Accent classes per audience. Kept as literal strings so Tailwind's content
 * scanner can see them.
 */
export const accentClasses: Record<
  AudienceAccent,
  { text: string; bgSoft: string; bg: string; bgHover: string; ring: string }
> = {
  traveler: {
    text: 'text-aud-traveler',
    bgSoft: 'bg-aud-traveler/10',
    bg: 'bg-aud-traveler',
    bgHover: 'hover:bg-aud-traveler/90',
    ring: 'ring-aud-traveler/25',
  },
  creator: {
    text: 'text-aud-creator',
    bgSoft: 'bg-aud-creator/10',
    bg: 'bg-aud-creator',
    bgHover: 'hover:bg-aud-creator/90',
    ring: 'ring-aud-creator/25',
  },
  guide: {
    text: 'text-aud-guide',
    bgSoft: 'bg-aud-guide/10',
    bg: 'bg-aud-guide',
    bgHover: 'hover:bg-aud-guide/90',
    ring: 'ring-aud-guide/25',
  },
  agency: {
    text: 'text-aud-agency',
    bgSoft: 'bg-aud-agency/10',
    bg: 'bg-aud-agency',
    bgHover: 'hover:bg-aud-agency/90',
    ring: 'ring-aud-agency/25',
  },
  hotel: {
    text: 'text-aud-hotel',
    bgSoft: 'bg-aud-hotel/10',
    bg: 'bg-aud-hotel',
    bgHover: 'hover:bg-aud-hotel/90',
    ring: 'ring-aud-hotel/25',
  },
  destination: {
    text: 'text-aud-destination',
    bgSoft: 'bg-aud-destination/10',
    bg: 'bg-aud-destination',
    bgHover: 'hover:bg-aud-destination/90',
    ring: 'ring-aud-destination/25',
  },
}

/** Small uppercase section label (WHY GWT, HOW IT WORKS, PRICING …). */
export function SectionLabel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p data-component-id="src/components/home/shared.tsx:139:4" data-component-path="src/components/home/shared.tsx" data-component-line="139" data-component-end-line="146" data-component-file="shared.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D"
      className={cn(
        'gwt-eyebrow text-[11px] font-semibold text-gwt-blue md:text-xs',
        className,
      )}
    >
      {children}
    </p>
  )
}

type SectionHeadingProps = {
  label?: string
  title: string
  supporting?: ReadonlyArray<string>
  align?: 'center' | 'left'
  tone?: 'light' | 'dark'
  className?: string
}

export function SectionHeading({
  label,
  title,
  supporting,
  align = 'center',
  tone = 'light',
  className,
}: SectionHeadingProps) {
  const dark = tone === 'dark'
  return (
    <div data-component-id="src/components/home/shared.tsx:169:4" data-component-path="src/components/home/shared.tsx" data-component-line="169" data-component-end-line="201" data-component-file="shared.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      className={cn(
        align === 'center' ? 'mx-auto max-w-3xl text-center' : 'max-w-3xl text-left',
        className,
      )}
    >
      {label ? (
        <SectionLabel data-component-id="src/components/home/shared.tsx:176:8" data-component-path="src/components/home/shared.tsx" data-component-line="176" data-component-end-line="176" data-component-file="shared.tsx" data-component-name="SectionLabel" data-component-content="%7B%22elementName%22%3A%22SectionLabel%22%7D" className={cn('mb-3', dark && 'text-gwt-gold')}>{label}</SectionLabel>
      ) : null}
      <h2 data-component-id="src/components/home/shared.tsx:178:6" data-component-path="src/components/home/shared.tsx" data-component-line="178" data-component-end-line="185" data-component-file="shared.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%7D"
        className={cn(
          'font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight md:text-[2.25rem]',
          dark ? 'text-white' : 'text-gwt-blue',
        )}
      >
        {title}
      </h2>
      {supporting?.length ? (
        <div data-component-id="src/components/home/shared.tsx:187:8" data-component-path="src/components/home/shared.tsx" data-component-line="187" data-component-end-line="199" data-component-file="shared.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={cn('mt-4 space-y-3', align === 'center' && 'mx-auto')}>
          {supporting.map((paragraph) => (
            <p data-component-id="src/components/home/shared.tsx:189:12" data-component-path="src/components/home/shared.tsx" data-component-line="189" data-component-end-line="197" data-component-file="shared.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D"
              key={paragraph}
              className={cn(
                'text-[0.9375rem] leading-relaxed md:text-base',
                dark ? 'text-white/75' : 'text-gwt-muted',
              )}
            >
              {paragraph}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** Page-width container matching the mock's content column. */
export function Container({ children, className }: { children: ReactNode; className?: string }) {
  return <div data-component-id="src/components/home/shared.tsx:207:9" data-component-path="src/components/home/shared.tsx" data-component-line="207" data-component-end-line="207" data-component-file="shared.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={cn('mx-auto w-full max-w-7xl px-5 md:px-8', className)}>{children}</div>
}

/** Pastel-tinted circular icon badge used throughout the mock. */
export function IconBadge({
  name,
  accent = 'traveler',
  size = 'md',
  className,
}: {
  name: string
  accent?: AudienceAccent
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  const a = accentClasses[accent]
  const dims =
    size === 'lg' ? 'h-16 w-16' : size === 'sm' ? 'h-10 w-10' : 'h-14 w-14'
  const icon = size === 'lg' ? 'h-7 w-7' : size === 'sm' ? 'h-[1.125rem] w-[1.125rem]' : 'h-6 w-6'
  return (
    <span data-component-id="src/components/home/shared.tsx:227:4" data-component-path="src/components/home/shared.tsx" data-component-line="227" data-component-end-line="237" data-component-file="shared.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full',
        dims,
        a.bgSoft,
        a.text,
        className,
      )}
    >
      <Icon data-component-id="src/components/home/shared.tsx:236:6" data-component-path="src/components/home/shared.tsx" data-component-line="236" data-component-end-line="236" data-component-file="shared.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22%5Bvar%3Aname%5D%22%2C%22className%22%3A%22%5Bvar%3Aicon%5D%22%7D" name={name} className={icon} />
    </span>
  )
}
